import {
  useBiIntegration,
  useCodeRepositoryIntegration,
  useDataCatalogIntegration,
  useMicrosoftTeamsIntegration,
  usePagerDutyIntegration,
  useTicketingIntegration,
  useOpsgenieIntegration,
  useServiceNowIntegration,
  useEnvironmentsSetup,
  useSlackIntegration,
} from 'shared/hooks/integrations';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useEnvIntegrations = ({ onUpdateSuccess, envId }: Props) => {
  const {
    environmentMutation,
    envScheduleMutation,
    deleteEnvironmentMutation,
  } = useEnvironmentsSetup({
    envId,
    onUpdateSuccess,
  });

  const {
    slackQuery,
    slackMutation,
    slackIntegrationMutation,
    isSlackConnected,
    slackChannelsMutation,
  } = useSlackIntegration({ envId, onUpdateSuccess });

  const {
    codeProjectsQuery,
    codeProjectsMutation,
    existingCodeProjects,
    codeProjectType,
    isRepositoryConnected,
    isLoading: isRepositoryLoading,
  } = useCodeRepositoryIntegration({
    envId,
    onUpdateSuccess,
    fetchFullData: true,
  });

  const {
    ticketingQuery,
    ticketingMutation,
    deleteTicketingMutation,
    ticketingProjectsMutation,
    isTicketingConnected,
  } = useTicketingIntegration({ envId, onUpdateSuccess });

  const {
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    isTeamsConnected,
    isTeamsLoading,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
  } = useMicrosoftTeamsIntegration({ envId, onUpdateSuccess });

  const {
    biIntegrationsQuery,
    biToolMutation,
    deleteBiIntegrationMutation,
    isBiToolConnected,
  } = useBiIntegration({ envId, onUpdateSuccess });

  const { dataCatalogMutation, dataCatalogConfigQuery } =
    useDataCatalogIntegration({ envId, onUpdateSuccess });

  const { pagerDutyQuery, pagerDutyIntegrationMutation, isPagerDutyConnected } =
    usePagerDutyIntegration();

  const { opsgenieQuery, opsgenieIntegrationMutation, isOpsgenieConnected } =
    useOpsgenieIntegration({ onUpdateSuccess });

  const { servicenowConfigQuery } = useServiceNowIntegration({ envId });

  const connectedMessagingTypes: AlertIntegrationType[] = [];
  if (isSlackConnected) connectedMessagingTypes.push('SLACK');
  if (isTeamsConnected) connectedMessagingTypes.push('MICROSOFT_TEAMS');

  return {
    slackQuery,
    slackMutation,
    slackIntegrationMutation,
    isSlackConnected,
    slackChannelsMutation,
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    isTeamsConnected,
    isTeamsLoading,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
    pagerDutyQuery,
    pagerDutyIntegrationMutation,
    opsgenieQuery,
    opsgenieIntegrationMutation,
    ticketingQuery,
    ticketingMutation,
    deleteTicketingMutation,
    ticketingProjectsMutation,
    isTicketingConnected,
    servicenowConfigQuery,
    biIntegrationsQuery,
    biToolMutation,
    deleteBiIntegrationMutation,
    isBiToolConnected,
    codeProjectsQuery,
    codeProjectsMutation,
    existingCodeProjects,
    codeProjectType,
    isRepositoryConnected,
    isRepositoryLoading,
    environmentMutation,
    envScheduleMutation,
    deleteEnvironmentMutation,
    isIncidentManagementConnected: isPagerDutyConnected || isOpsgenieConnected,
    dataCatalogMutation,
    dataCatalogConfigQuery,
    connectedMessagingTypes,
  };
};

export default useEnvIntegrations;
